import { useUsers, userListItemToView } from '@/domains/users';
import { useDebouncedValue } from '@/shared/hooks';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Loader2, Mail, Search, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';

interface AddTeamMembersModalProps {
  open: boolean;
  onClose: () => void;
  existingMemberIds: Set<string>;
  onAddMembers: (userIds: number[]) => Promise<void>;
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

export function AddTeamMembersModal({
  open,
  onClose,
  existingMemberIds,
  onAddMembers,
}: AddTeamMembersModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedSearch = useDebouncedValue(searchTerm, 500);

  const { data: usersData, isFetching } = useUsers(
    { status: 'active', search: debouncedSearch || undefined, limit: '50' },
    open,
  );

  const availableUsers = useMemo(() => {
    const all = usersData?.users?.map(userListItemToView) ?? [];
    return all.filter((u) => !existingMemberIds.has(u.id));
  }, [usersData, existingMemberIds]);

  const toggle = (userId: string) => {
    setSelectedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedIds([]);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedIds.length) return;
    setIsSubmitting(true);
    try {
      await onAddMembers(selectedIds.map(Number));
      showSuccessToast('Members Added', `${selectedIds.length} member(s) added to team.`);
      handleClose();
    } catch (error: any) {
      showErrorToast('Failed to Add Members', error?.message || 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent
        className="max-w-2xl max-h-[80vh] flex flex-col p-0"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="p-4 border-b border-border">
          <DialogTitle>Add Team Members</DialogTitle>
          <DialogDescription>Select multiple users to add to the team</DialogDescription>
        </DialogHeader>

        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name, email, or team..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          {selectedIds.length > 0 && (
            <div className="mt-2 text-sm text-muted-foreground">
              {selectedIds.length} user{selectedIds.length !== 1 ? 's' : ''} selected
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {isFetching &&
              [...Array(50)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-muted animate-pulse flex-shrink-0" />
                    <div className="space-y-1.5">
                      <div className="h-3.5 w-32 rounded bg-muted animate-pulse" />
                      <div className="h-3 w-48 rounded bg-muted animate-pulse" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-16 rounded-full bg-muted animate-pulse" />
                  </div>
                </div>
              ))}
            {!isFetching && availableUsers.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No available users found</p>
              </div>
            )}
            {!isFetching &&
              availableUsers.map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                    selectedIds.includes(user.id)
                      ? 'border-orange-600 bg-orange-50 dark:bg-orange-950/20'
                      : 'border-border hover:bg-muted/50'
                  }`}
                  onClick={() => toggle(user.id)}
                >
                  <div className="flex items-center gap-3">
                    <Avatar
                      className={`w-9 h-9 ${selectedIds.includes(user.id) ? 'bg-orange-600' : 'bg-blue-600'} text-white`}
                    >
                      <AvatarFallback
                        className={`${selectedIds.includes(user.id) ? 'bg-orange-600' : 'bg-blue-600'} text-white text-sm`}
                      >
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium text-foreground text-sm">{user.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <Mail className="w-3 h-3" />
                        {user.email}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedIds.includes(user.id) && (
                      <Badge className="bg-orange-600 text-white border-0">Selected</Badge>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {user.role}
                    </Badge>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div className="flex items-center justify-between p-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {selectedIds.length > 0 ? (
              <span className="font-medium text-foreground">
                {selectedIds.length} user{selectedIds.length !== 1 ? 's' : ''} ready to add
              </span>
            ) : (
              'Select users to add to the team'
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={handleSubmit}
              disabled={selectedIds.length === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Add {selectedIds.length > 0 ? `${selectedIds.length} ` : ''}
              Member{selectedIds.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
